import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Plus, RefreshCw, Trash2, Filter, RotateCcw, ChevronsUpDown, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { toast } from 'sonner';
import { formatPrice, formatQty } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const POProductCombobox = ({ products, value, onChange, getStockForProduct, getThresholdForProduct, index }) => {
  const [open, setOpen] = useState(false);
  const selected = products.find(p => p.product_id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={`po-product-select-${index}`}
          className="w-full justify-between border-stone-200 font-normal h-8 text-xs"
        >
          {selected ? (
            <span className="truncate">{selected.product_name} - {selected.product_brand} ({selected.product_size}, {selected.product_color})</span>
          ) : (
            <span className="text-stone-500">Search & select product...</span>
          )}
          <ChevronsUpDown className="ml-2 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start" side="bottom" avoidCollisions={true} sideOffset={4}>
        <Command>
          <CommandInput placeholder="Type to search product..." />
          <CommandList className="max-h-[200px] overflow-y-auto">
            <CommandEmpty>No product found.</CommandEmpty>
            <CommandGroup>
              {products.filter(p => p.is_active !== false || p.product_id === value).map((product) => {
                const stock = getStockForProduct(product.product_id);
                const stockColor = stock === 0 ? 'text-red-600' : 'text-stone-500';
                return (
                  <CommandItem
                    key={product.product_id}
                    value={`${product.product_name} ${product.product_brand} ${product.product_size} ${product.product_color}`}
                    onSelect={() => {
                      onChange(product.product_id);
                      setOpen(false);
                      setTimeout(() => {
                        const qtyInput = document.querySelector(`[data-testid="po-qty-input-${index}"]`);
                        if (qtyInput) { qtyInput.focus(); qtyInput.select(); }
                      }, 50);
                    }}
                  >
                    <Check className={`mr-2 h-3 w-3 ${value === product.product_id ? 'opacity-100' : 'opacity-0'}`} />
                    <span className="flex-1 truncate text-xs">{product.product_name} - {product.product_brand} ({product.product_size}, {product.product_color})</span>
                    <span className={`font-medium ${stockColor} text-xs ml-2`}>Stock: {stock ?? '-'}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const PurchaseOrders = () => {
  const { user } = useOutletContext();
  const isAdmin = user?.role === 'admin';

  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Flat items + filters
  const [flatItems, setFlatItems] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [vendorFilter, setVendorFilter] = useState('ALL');

  // Create form
  const [selectedVendor, setSelectedVendor] = useState('');
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [poItems, setPoItems] = useState([{ product_id: '', quantity: 1, remarks: '' }]);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [posRes, vendorsRes, productsRes, invRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/purchase-orders`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/vendors`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/products`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/inventory`, { withCredentials: true })
      ]);
      setPurchaseOrders(posRes.data);
      setVendors(vendorsRes.data);
      setProducts(productsRes.data);
      setInventory(invRes.data);

      // Build flat items
      const allFlat = [];
      for (const po of posRes.data) {
        for (const item of (po.items || [])) {
          const inv = invRes.data.find(i => i.product_id === item.product_id);
          allFlat.push({
            ...item,
            purchase_order_id: po.purchase_order_id,
            vendor_id: po.vendor_id,
            vendor_name: po.vendor_name,
            order_date: po.order_date,
            notes: po.notes,
            available_stock: inv?.current_stock || 0
          });
        }
      }
      setFlatItems(allFlat);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const vendorProducts = products.filter(p => p.vendor_id === selectedVendor);

  const getStockForProduct = (productId) => {
    const inv = inventory.find(i => i.product_id === productId);
    return inv ? inv.current_stock : 0;
  };

  const getThresholdForProduct = (productId) => {
    const inv = inventory.find(i => i.product_id === productId);
    return inv ? (inv.min_stock_threshold || 0) : 0;
  };

  const addItem = () => setPoItems([...poItems, { product_id: '', quantity: 1, remarks: '' }]);
  const removeItem = (index) => { if (poItems.length > 1) setPoItems(poItems.filter((_, i) => i !== index)); };
  const updateItem = (index, field, value) => {
    const u = [...poItems];
    u[index][field] = value;
    // Auto-add next line when product is selected on the last row
    if (field === 'product_id' && value && index === u.length - 1) {
      u.push({ product_id: '', quantity: 1, remarks: '' });
    }
    setPoItems(u);
  };

  const [editingQty, setEditingQty] = useState({ itemId: null, field: null, value: '' });

  const handleQtyEditPO = async (itemId, field, newValue) => {
    const val = parseFloat(newValue);
    if (isNaN(val) || val < 0) { toast.error('Invalid quantity'); return; }
    try {
      await axios.patch(`${BACKEND_URL}/api/order-items/${itemId}/edit-qty`, { field, value: val }, { withCredentials: true });
      toast.success('Quantity updated');
      setEditingQty({ itemId: null, field: null, value: '' });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const resetForm = () => {
    setSelectedVendor('');
    setOrderDate(new Date().toISOString().split('T')[0]);
    setNotes('');
    setPoItems([{ product_id: '', quantity: 1, remarks: '' }]);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const validItems = poItems.filter(i => i.product_id && i.quantity > 0);
    if (validItems.length === 0) { toast.error('Add at least one product'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/purchase-orders`, {
        vendor_id: selectedVendor, order_date: orderDate, items: validItems, notes: notes || null
      }, { withCredentials: true });
      toast.success('Purchase order created');
      setCreateDialogOpen(false);
      resetForm();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create purchase order');
    }
  };

  const [poQtyDialog, setPoQtyDialog] = useState({ open: false, poItemId: '', status: '', qty: 0, ordered: 0 });

  const handleItemStatusUpdate = async (poItemId, newStatus, item) => {
    const QTY_STATUSES = ['Dispatched from Vendor', 'Received in Store', 'Delivered'];
    if (QTY_STATUSES.includes(newStatus)) {
      let defaultQty = item.quantity;
      if (newStatus === 'Received in Store') defaultQty = item.dispatched_qty || item.quantity;
      if (newStatus === 'Delivered') defaultQty = item.received_qty || item.quantity;
      setPoQtyDialog({ open: true, poItemId, status: newStatus, qty: defaultQty, ordered: item.quantity });
      return;
    }
    try {
      await axios.patch(`${BACKEND_URL}/api/order-items/${poItemId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success('Status updated');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const confirmPoQtyStatus = async () => {
    try {
      await axios.patch(`${BACKEND_URL}/api/order-items/${poQtyDialog.poItemId}/status`, { status: poQtyDialog.status, actual_qty: poQtyDialog.qty }, { withCredentials: true });
      toast.success('Status updated');
      fetchAll();
      setPoQtyDialog({ open: false, poItemId: '', status: '', qty: 0, ordered: 0 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDeletePO = async (poId) => {
    if (!window.confirm('Delete this purchase order and all its items?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/purchase-orders/${poId}`, { withCredentials: true });
      toast.success('Purchase order deleted');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleReorderPO = async (poItemId) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/order-items/${poItemId}/reorder`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create reorder');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Ordered from Vendor': 'bg-blue-100 text-blue-800',
      'Dispatched from Vendor': 'bg-amber-100 text-amber-800',
      'Received in Store': 'bg-lime-100 text-lime-800',
      'Completed': 'bg-emerald-100 text-emerald-800',
      'Delivered': 'bg-lime-100 text-lime-800',
      'Cancelled': 'bg-red-100 text-red-800',
      'Pending': 'bg-stone-100 text-stone-800',
      'Ready for Delivery': 'bg-amber-100 text-amber-800'
    };
    return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-stone-100 text-stone-800'}`}>{status}</span>;
  };

  const pendingStatuses = ['Ordered from Vendor', 'Dispatched from Vendor', 'Pending', 'Ready for Delivery'];
  const allPoStatuses = ['Ordered from Vendor', 'Dispatched from Vendor', 'Received in Store', 'Completed', 'Cancelled'];
  const lowStockProducts = inventory.filter(i => (i.min_stock_threshold > 0 && i.current_stock <= i.min_stock_threshold) || i.current_stock === 0);

  // Apply filters
  let filtered = [...flatItems];
  if (searchFilter) {
    const keywords = searchFilter.toLowerCase().trim().split(/\s+/);
    filtered = filtered.filter(item => {
      const str = `${item.product_name} ${item.product_brand} ${item.product_size} ${item.product_color}`.toLowerCase();
      return keywords.every(kw => str.includes(kw));
    });
  }
  if (statusFilter === 'PENDING') {
    filtered = filtered.filter(item => pendingStatuses.includes(item.status));
  } else if (statusFilter !== 'ALL') {
    filtered = filtered.filter(item => item.status === statusFilter);
  }
  if (vendorFilter !== 'ALL') {
    filtered = filtered.filter(item => item.vendor_id === vendorFilter);
  }

  if (loading) return <div className="text-stone-600 text-sm">Loading purchase orders...</div>;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-stone-900" data-testid="purchase-orders-title" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Purchase Orders
          </h1>
          <p className="text-xs text-stone-600 mt-1">{filtered.length} items {(searchFilter || statusFilter !== 'ALL' || vendorFilter !== 'ALL') ? '(filtered)' : 'total'}</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="refresh-po-btn" onClick={fetchAll} variant="outline" size="sm" className="border-stone-200 text-stone-700 hover:bg-stone-50 text-xs h-8">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
          <Button data-testid="create-po-btn" onClick={() => setCreateDialogOpen(true)} size="sm" className="bg-amber-700 hover:bg-amber-800 text-white text-xs h-8">
            <Plus className="w-3 h-3 mr-1" /> New Purchase Order
          </Button>
        </div>
      </div>

      {/* Low stock hint */}
      {lowStockProducts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800">
          <span className="font-semibold">{lowStockProducts.length} products</span> are low or out of stock. Create a purchase order to restock.
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-stone-200 rounded-md p-3">
        <div className="flex gap-2 items-center">
          <Button
            data-testid="po-pending-filter"
            onClick={() => setStatusFilter(statusFilter === 'PENDING' ? 'ALL' : 'PENDING')}
            variant={statusFilter === 'PENDING' ? 'default' : 'outline'}
            size="sm"
            className={`text-xs h-8 whitespace-nowrap ${statusFilter === 'PENDING' ? 'bg-amber-700 hover:bg-amber-800 text-white' : 'border-stone-200 text-stone-700'}`}
          >
            Pending Only
          </Button>
          <Input
            placeholder="Search product (name, brand, size, color)..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="text-xs h-8 border-stone-200 flex-1"
            data-testid="po-search-product"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="text-xs h-8 border-stone-200 w-40" data-testid="po-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL" className="text-xs">All Statuses</SelectItem>
              <SelectItem value="PENDING" className="text-xs">Pending (In Progress)</SelectItem>
              {allPoStatuses.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={vendorFilter} onValueChange={setVendorFilter}>
            <SelectTrigger className="text-xs h-8 border-stone-200 w-40" data-testid="po-vendor-filter">
              <SelectValue placeholder="Vendor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL" className="text-xs">All Vendors</SelectItem>
              {vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id} className="text-xs">{v.vendor_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Create PO Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={(open) => { setCreateDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Purchase Order</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vendor *</Label>
                <Select value={selectedVendor} onValueChange={(v) => { setSelectedVendor(v); setPoItems([{ product_id: '', quantity: 1, remarks: '' }]); }}>
                  <SelectTrigger data-testid="po-vendor-select" className="border-stone-200 text-sm"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>{vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Order Date *</Label>
                <Input data-testid="po-date-input" type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} required className="border-stone-200" />
              </div>
            </div>
            {selectedVendor && (
              <>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label className="text-sm font-semibold">Products</Label>
                    <Button type="button" data-testid="add-po-item-btn" onClick={addItem} variant="outline" size="sm" className="text-xs h-7 border-stone-300"><Plus className="w-3 h-3 mr-1" /> Add Product</Button>
                  </div>
                  <div className="space-y-2">
                    {poItems.map((item, idx) => {
                      const stock = item.product_id ? getStockForProduct(item.product_id) : null;
                      const threshold = item.product_id ? getThresholdForProduct(item.product_id) : null;
                      const selectedProduct = vendorProducts.find(p => p.product_id === item.product_id);
                      return (
                        <div key={idx} className="flex gap-2 items-start bg-stone-50 p-2 rounded border border-stone-200">
                          <div className="flex-1">
                            <POProductCombobox
                              products={vendorProducts}
                              value={item.product_id}
                              onChange={(v) => updateItem(idx, 'product_id', v)}
                              getStockForProduct={getStockForProduct}
                              getThresholdForProduct={getThresholdForProduct}
                              index={idx}
                            />
                            {stock !== null && (
                              <div className="text-xs mt-1 text-stone-500">
                                Current stock: <span className={stock === 0 ? 'text-red-600 font-medium' : (threshold && stock <= threshold) ? 'text-amber-600 font-medium' : 'text-stone-700'}>{stock}</span>
                                {threshold > 0 && <span className="ml-2">Min: {threshold}</span>}
                              </div>
                            )}
                          </div>
                          <div className="w-20">
                            <Input data-testid={`po-qty-input-${idx}`} type="number" min="1" step="0.01" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)} className="border-stone-200 text-xs h-8" placeholder="Qty" />
                          </div>
                          <div className="w-28">
                            <Input data-testid={`po-remarks-input-${idx}`} value={item.remarks} onChange={(e) => updateItem(idx, 'remarks', e.target.value)} className="border-stone-200 text-xs h-8" placeholder="Remarks" />
                          </div>
                          {poItems.length > 1 && (
                            <Button type="button" onClick={() => removeItem(idx)} variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-8 w-8 p-0"><Trash2 className="w-3 h-3" /></Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label>Notes (optional)</Label>
                  <Input data-testid="po-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} className="border-stone-200" placeholder="Any notes for this order" />
                </div>
              </>
            )}
            <Button type="submit" data-testid="submit-po-btn" disabled={!selectedVendor || poItems.every(i => !i.product_id)} className="w-full bg-amber-700 hover:bg-amber-800 text-white">Create Purchase Order</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Flat Items Table */}
      {flatItems.length === 0 ? (
        <div className="bg-white rounded-lg border border-stone-200 p-12 text-center">
          <h3 className="text-lg font-medium text-stone-900 mb-2">No Purchase Orders</h3>
          <p className="text-stone-600 text-sm">Create a purchase order to restock your inventory from vendors.</p>
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 320px)' }}>
            <table className="w-full text-xs border-collapse">
              <thead className="bg-stone-100 sticky top-0 z-10">
                <tr className="border-b border-stone-300">
                  <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-48">PRODUCT</th>
                  <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-28">VENDOR</th>
                  <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-24">PO ID</th>
                  <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-24">DATE</th>
                  <th className="border-r border-stone-300 p-2 text-right font-semibold text-stone-700 w-14">ORD.</th>
                  <th className="border-r border-stone-300 p-2 text-right font-semibold text-stone-700 w-14">DISP.</th>
                  <th className="border-r border-stone-300 p-2 text-right font-semibold text-stone-700 w-14">RCVD.</th>
                  <th className="border-r border-stone-300 p-2 text-right font-semibold text-stone-700 w-14">DLVD.</th>
                  {isAdmin && <th className="border-r border-stone-300 p-2 text-right font-semibold text-stone-700 w-24">COST</th>}
                  <th className="border-r border-stone-300 p-2 text-right font-semibold text-stone-700 w-20">STOCK</th>
                  <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-28">STATUS</th>
                  <th className="border-r border-stone-300 p-2 text-left font-semibold text-stone-700 w-36">UPDATE STATUS</th>
                  <th className="p-2 text-right font-semibold text-stone-700 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={isAdmin ? 13 : 12} className="p-6 text-center text-stone-500 text-xs">No items match your filters.</td></tr>
                ) : (
                  filtered.map((item, index) => (
                    <tr
                      key={item.po_item_id}
                      data-testid={`poi-row-${item.po_item_id}`}
                      className={`border-b border-stone-200 hover:bg-stone-50 ${index % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'}`}
                    >
                      <td className="border-r border-stone-200 p-2 w-48">
                        <div className="font-medium text-stone-900">{item.product_name}</div>
                        <div className="text-stone-600">{item.product_brand} {item.product_size && `\u2022 ${item.product_size}`} {item.product_color && `\u2022 ${item.product_color}`}</div>
                        {(() => {
                          const rcvd = item.received_qty || 0;
                          const isTerminal = ['Received in Store', 'Completed', 'Cancelled'].includes(item.status);
                          const hasShortfall = isTerminal && rcvd > 0 && rcvd < item.quantity;
                          if (!hasShortfall) return null;
                          const gap = item.quantity - rcvd;
                          const alreadyReordered = flatItems.some(
                            sibling => sibling.remarks === `Reorder: shortfall from ${item.po_item_id}`
                          );
                          return (
                            <div className="flex items-center gap-1.5 mt-1">
                              <span data-testid={`po-shortfall-badge-${item.po_item_id}`} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-800 border border-orange-200">
                                {gap} pending
                              </span>
                              {alreadyReordered ? (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-stone-100 text-stone-500 border border-stone-200">
                                  Reordered
                                </span>
                              ) : (
                                <button
                                  data-testid={`po-reorder-btn-${item.po_item_id}`}
                                  onClick={() => handleReorderPO(item.po_item_id)}
                                  className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200 transition-colors"
                                >
                                  <RotateCcw className="w-2.5 h-2.5" /> Reorder
                                </button>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="border-r border-stone-200 p-2 text-stone-700 w-28">{item.vendor_name}</td>
                      <td className="border-r border-stone-200 p-2 text-stone-600 font-mono w-24">{item.purchase_order_id}</td>
                      <td className="border-r border-stone-200 p-2 text-stone-700 tabular-nums w-24">{item.order_date}</td>
                      <td className="border-r border-stone-200 p-2 text-right font-medium text-stone-900 tabular-nums w-14">{formatQty(item.quantity)}</td>
                      <td className="border-r border-stone-200 p-2 text-right tabular-nums w-14">{item.dispatched_qty ? formatQty(item.dispatched_qty) : '-'}</td>
                      <td className="border-r border-stone-200 p-2 text-right tabular-nums w-14">
                        {editingQty.itemId === item.po_item_id && editingQty.field === 'received_qty' ? (
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            autoFocus
                            className="w-14 text-right text-xs border border-blue-300 rounded px-1 py-0.5 bg-blue-50"
                            value={editingQty.value}
                            onChange={(e) => setEditingQty({ ...editingQty, value: e.target.value })}
                            onBlur={() => handleQtyEditPO(item.po_item_id, 'received_qty', editingQty.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleQtyEditPO(item.po_item_id, 'received_qty', editingQty.value); if (e.key === 'Escape') setEditingQty({ itemId: null, field: null, value: '' }); }}
                          />
                        ) : (
                          <span
                            className={`cursor-pointer hover:bg-blue-50 hover:text-blue-800 px-1 rounded ${item.received_qty ? '' : 'text-stone-400'}`}
                            onClick={() => setEditingQty({ itemId: item.po_item_id, field: 'received_qty', value: item.received_qty || '' })}
                            title="Click to edit received qty"
                          >
                            {item.received_qty ? formatQty(item.received_qty) : '-'}
                          </span>
                        )}
                      </td>
                      <td className="border-r border-stone-200 p-2 text-right tabular-nums w-14">{item.delivered_qty ? formatQty(item.delivered_qty) : '-'}</td>
                      {isAdmin && (
                        <td className="border-r border-stone-200 p-2 text-right text-stone-900 tabular-nums w-24">{formatPrice(item.total_price)}</td>
                      )}
                      <td className="border-r border-stone-200 p-2 text-right tabular-nums w-20">
                        <span className={item.available_stock >= item.quantity ? 'text-lime-700 font-semibold' : 'text-red-700 font-semibold'}>
                          {formatQty(item.available_stock)}
                        </span>
                      </td>
                      <td className="border-r border-stone-200 p-2 w-28">{getStatusBadge(item.status)}</td>
                      <td className="border-r border-stone-200 p-2 w-36">
                        {['Delivered', 'Cancelled'].includes(item.status) ? (
                          <span className="text-stone-400 text-xs">Final</span>
                        ) : (
                          <Select value={item.status} onValueChange={(value) => handleItemStatusUpdate(item.po_item_id, value, item)}>
                            <SelectTrigger data-testid={`poi-status-select-${item.po_item_id}`} className="w-full border-stone-200 text-xs h-7">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Ordered from Vendor" className="text-xs">Ordered from Vendor</SelectItem>
                              <SelectItem value="Dispatched from Vendor" className="text-xs">Dispatched from Vendor</SelectItem>
                              <SelectItem value="Received in Store" className="text-xs">Received in Store</SelectItem>
                              <SelectItem value="Completed" className="text-xs">Completed</SelectItem>
                              <SelectItem value="Cancelled" className="text-xs">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </td>
                      <td className="p-2 text-right w-10">
                        {!['Delivered', 'Cancelled'].includes(item.status) && (
                          <button data-testid={`po-delete-${item.purchase_order_id}`} onClick={() => handleDeletePO(item.purchase_order_id)} className="text-red-500 hover:text-red-700">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="bg-stone-50 border border-stone-200 rounded-md p-2 flex justify-between items-center text-xs">
        <div className="text-stone-600">
          Showing: <span className="font-semibold text-stone-900">{filtered.length}</span> items
        </div>
        <div className="text-stone-500">
          {(searchFilter || statusFilter !== 'ALL' || vendorFilter !== 'ALL') ? 'Showing filtered results' : 'Showing all items'}
        </div>
      </div>

      {/* Quantity confirmation dialog */}
      <Dialog open={poQtyDialog.open} onOpenChange={(open) => { if (!open) setPoQtyDialog(prev => ({ ...prev, open: false })); }}>
        <DialogContent className="bg-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">Enter Actual Quantity</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-stone-600">
              Status: <span className="font-semibold text-stone-900">{poQtyDialog.status}</span>
            </p>
            <p className="text-sm text-stone-600">
              Ordered: <span className="font-semibold text-stone-900">{poQtyDialog.ordered}</span>
            </p>
            <div>
              <Label>Actual Quantity</Label>
              <Input
                data-testid="po-qty-dialog-input"
                type="number"
                step="0.01"
                min="0"
                value={poQtyDialog.qty}
                onChange={(e) => setPoQtyDialog(prev => ({ ...prev, qty: parseFloat(e.target.value) || 0 }))}
                className="border-stone-200"
                autoFocus
              />
            </div>
            <Button data-testid="po-qty-dialog-confirm" onClick={confirmPoQtyStatus} className="w-full bg-amber-700 hover:bg-amber-800 text-white">
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrders;
